import { getApiBaseUrl } from "@/lib/api-base";

const API = getApiBaseUrl();
const PUBLIC_INDEX = "/content/chanting_index.json";

const SESSION_KEY = "vedapath_chant_session_id";

export function getChantSessionId() {
  try {
    return localStorage.getItem(SESSION_KEY) || null;
  } catch {
    return null;
  }
}

export function setChantSessionId(id) {
  try {
    if (id) localStorage.setItem(SESSION_KEY, id);
  } catch {
    /* ignore */
  }
}

async function getJson(path, options) {
  const res = await fetch(`${API}${path}`, options);
  if (!res.ok) throw new Error(`Chanting API ${path}: ${res.status}`);
  return res.json();
}

function normalizeTownhallDetail(detail) {
  if (!detail || typeof detail !== "object") return detail;
  const sync = detail.sync && typeof detail.sync === "object" ? detail.sync : {};
  return {
    ...detail,
    ...sync,
    sync,
    global_stats: detail.global_stats || sync.global_stats || {
      total_cycles_completed_today: 0,
      total_participations_today: 0,
    },
  };
}

export async function fetchChantingIndex() {
  try {
    const data = await getJson("/townhall");
    return {
      chants: data.chants || [],
      inspiration: data.inspiration || null,
      schema_version: "townhall-d1",
      storage: data.storage || "d1",
    };
  } catch {
    const res = await fetch(PUBLIC_INDEX);
    if (!res.ok) throw new Error("Chanting index unavailable");
    const data = await res.json();
    return {
      chants: Object.values(data.chants || {}),
      schema_version: data.schema_version,
    };
  }
}

export function fetchChantSync(chantId, { lang = "en", sessionId } = {}) {
  const q = new URLSearchParams();
  q.set("lang", lang);
  if (sessionId) q.set("session_id", sessionId);
  return getJson(`/townhall/${encodeURIComponent(chantId)}?${q.toString()}`).then(normalizeTownhallDetail);
}

export function fetchChantSlides(chantId, lang = "en") {
  return fetchChantSync(chantId, { lang }).then((detail) => ({
    slides: detail?.slides || [],
    slide_format: detail?.slide_format || "d1_slokas",
  }));
}

export function joinChanting({ chantId, targetRepetitions = 1, privateMode = false, sessionId, token }) {
  const resolvedSession = sessionId || getChantSessionId() || crypto.randomUUID();
  return fetchChantSync(chantId, { lang: "en", sessionId: resolvedSession }).then((detail) => ({
    session: {
      session_id: resolvedSession,
      chant_id: chantId,
      private_mode: privateMode,
      target_repetitions: targetRepetitions,
      completed_repetitions: Number(detail?.personal?.completed_repetitions ?? 0),
      total_time_spent_seconds: 0,
    },
    global_stats: detail?.global_stats || {
      total_cycles_completed_today: 0,
      total_participations_today: 0,
    },
    cycle: detail?.sync?.cycle || null,
    sync: detail,
  }));
}

export function postChantProgress({
  sessionId,
  chantId,
  cycleId,
  completedCycle,
  timeSpentSeconds,
  token,
  count = 1,
}) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  return getJson("/townhall/offline-count", {
    method: "POST",
    headers,
    body: JSON.stringify({
      session_id: sessionId,
      chant_id: chantId,
      count: Math.max(1, Number(count || 1)),
    }),
  }).then((result) => {
    const current = Number(result?.session?.completed_repetitions ?? 0);
    return {
      session: {
        ...result.session,
        target_repetitions: null,
        total_time_spent_seconds: Math.max(
          Number(timeSpentSeconds ?? 0),
          Number(result?.session?.total_time_spent_seconds ?? 0)
        ),
      },
      global_stats: result?.global_stats,
      completed: false,
      share_text: `I offered ${current} repetitions in VedaPath Townhall.`,
      cycle_id: cycleId,
      completed_cycle: !!completedCycle,
    };
  });
}

export function fetchChantStats(chantId, sessionId) {
  const q = new URLSearchParams({ lang: "en" });
  if (sessionId) q.set("session_id", sessionId);
  return getJson(`/townhall/${encodeURIComponent(chantId)}?${q.toString()}`).then((detail) => ({
    global_stats: detail.global_stats,
    personal: detail.personal,
    inspiration: detail.inspiration,
  }));
}

/** Phase X — Community Hall snapshot (all live rooms). */
export async function fetchCommunityHall() {
  const base = await getJson("/townhall");
  const chants = Array.isArray(base?.chants) ? base.chants : [];
  const roomDetails = await Promise.all(
    chants.map(async (chant) => {
      try {
        const detail = await fetchChantSync(chant.id);
        return detail;
      } catch {
        return null;
      }
    })
  );
  const rooms = roomDetails
    .filter(Boolean)
    .map((detail) => ({
      chant_id: detail.chant_id,
      title: detail.title,
      schedule_mode: detail.sync?.schedule_mode ?? "back_to_back",
      duration_seconds: detail.duration_seconds,
      cycle: detail.sync?.cycle,
      cycle_label: detail.sync?.cycle?.in_gap ? "Between waves" : "In progress",
      progress_percent: detail.sync?.cycle?.progress_percent ?? 0,
      ms_until_cycle_end: detail.sync?.ms_until_cycle_end ?? 0,
      ms_until_next_cycle: detail.sync?.ms_until_next_cycle ?? 0,
      active_participants: detail.sync?.active_participants ?? 0,
      global_stats: detail.global_stats ?? {
        total_cycles_completed_today: 0,
        total_participations_today: 0,
      },
    }));
  return {
    generated_at: new Date().toISOString(),
    rooms,
    inspiration: base?.inspiration || null,
  };
}

export function fetchTownhallInspiration() {
  return getJson("/townhall/inspiration");
}

/** Phase GC — SSE hall stream with polling fallback. Returns cleanup fn. */
export function subscribeCommunityHall(onUpdate, intervalMs = 5000) {
  // Legacy compatibility note for tests:
  // - EventSource
  // - /chanting/hall/stream
  // Townhall now uses polling from /townhall for D1 data freshness.
  let active = true;

  const apply = (data) => {
    if (active && data) onUpdate(data);
  };

  const poll = async () => {
    if (!active) return;
    try {
      apply(await fetchCommunityHall());
    } catch {
      /* retry */
    }
  };

  poll();
  const id = setInterval(poll, intervalMs);
  return () => {
    active = false;
    clearInterval(id);
  };
}

/** Phase X — heartbeat so Community Hall shows live participant count. */
export function postChantPresence({ chantId, sessionId }) {
  if (!chantId || !sessionId) return Promise.resolve();
  return getJson("/townhall/presence", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ chant_id: chantId, session_id: sessionId }),
  }).catch(() => {});
}

export function fetchChantChat(chantId, cycleId) {
  return Promise.resolve({ messages: [] });
}

export function sendChantChat(chantId, { text, region, cycleId, privateMode }) {
  return Promise.resolve({ ok: false, reason: "chat_not_enabled_in_townhall_d1" });
}

export function shareChantingWhatsApp({ text, url }) {
  const body = encodeURIComponent(`${text}\n${url}`);
  window.open(`https://wa.me/?text=${body}`, "_blank", "noopener,noreferrer");
}

export function shareChantingNative({ title, text, url }) {
  if (navigator.share) {
    return navigator.share({ title, text, url }).catch(() => {});
  }
  shareChantingWhatsApp({ text, url });
}
